/**
 * dataCenterLibrary — selectable data-centre presets that DRIVE GENERATION DUTY.
 *
 * A data centre is the LOAD the plant exists to serve, so picking one here sets
 * the scenario's facility/critical load, which is what the generation solver
 * sizes against. The presets are derived from the platform's existing
 * {@link WORKLOAD_PROFILES} (one per workload type, carrying the authoritative
 * kW/rack density anchors) and ranked against the site's Project Intent, so the
 * profile the user already declared is offered first.
 *
 * Power arithmetic is delegated to {@link resolvePowerStack} — the single
 * platform owner of the IT → facility → grid ladder (PUE, power factor). This
 * module never re-implements that conversion; it only chooses the IT load:
 *
 *   IT MW = racks × kW/rack ÷ 1000        (rack density from the workload profile)
 *   facility MW, grid MW                  (resolvePowerStack, via PUE + PF)
 *
 * Nothing here mutates a scenario on its own — `applyDataCenterLoad` returns a
 * new input, leaving the caller to re-solve.
 */
import {
  WORKLOAD_PROFILES,
  type EffectiveWorkloadProfile,
  type WorkloadProfile,
  type WorkloadProfileEntry,
} from '../workloadProfile';
import { resolvePowerStack, type PowerStack } from '../powerStack';
import { convertLitresPerKwhItToGalPerMwhFacility } from './screeningEvidencePrefill';
import type { GenerationImpactScenarioInput } from '../../types/generationImpact';

/** A selectable data-centre preset, sized by rack count. */
export interface DataCenterPreset {
  id: WorkloadProfile;
  label: string;
  hint: string;
  /** kW per rack anchor for this workload (min/max from the profile). */
  powerDensityKwRack: { min: number; max: number };
  /** Mid-band density used when the user has not overridden it. */
  defaultKwPerRack: number;
  /** True when this preset matches the site's declared project intent. */
  recommended: boolean;
  /** Why it is recommended (empty when it is not). */
  recommendedReason?: string;
}

/** A sized data centre — the load a scenario's generation must serve. */
export interface DataCenterSizing {
  presetId: WorkloadProfile;
  label: string;
  rackCount: number;
  kwPerRack: number;
  /** IT (compute) load — racks × kW/rack. */
  itLoadMW: number;
  /** Facility load after PUE, and the grid draw after power factor. */
  facilityMW: number;
  gridMW: number | null;
  pue: number;
  power: PowerStack;
}

function midBandDensity(entry: WorkloadProfileEntry): number {
  return (entry.powerDensityKwRack.min + entry.powerDensityKwRack.max) / 2;
}

/**
 * The library, ranked for a site: the workload profile the project intent
 * already declares is marked `recommended` and sorted first. Everything else
 * keeps the canonical `WORKLOAD_PROFILES` order so the list is stable.
 */
export function dataCenterPresetsForIntent(
  intendedWorkload?: EffectiveWorkloadProfile | null,
): DataCenterPreset[] {
  const presets = WORKLOAD_PROFILES.map<DataCenterPreset>(entry => {
    const recommended = intendedWorkload != null
      && intendedWorkload !== 'unknown'
      && entry.id === intendedWorkload;
    return {
      id: entry.id,
      label: entry.label,
      hint: entry.hint,
      powerDensityKwRack: { ...entry.powerDensityKwRack },
      defaultKwPerRack: midBandDensity(entry),
      recommended,
      ...(recommended
        ? { recommendedReason: 'Matches the workload profile declared in Project Intent' }
        : {}),
    };
  });
  // Stable partition — recommended first, canonical order preserved within each.
  return [...presets.filter(p => p.recommended), ...presets.filter(p => !p.recommended)];
}

/**
 * P5 (2026-09-05) — recommend a cooling FAMILY for the water-capacity fit, the
 * way `dataCenterPresetsForIntent` recommends a workload profile. Driven by the
 * documented water limit, never by the equipment (cooling is chosen in the
 * requirements stage, two stages before equipment), and honest over helpful:
 * with no documented limit — or no declared heat-rejection sink to reason from
 * — there is NO recommendation rather than a partial one.
 *
 *   fits      → the family the declared sink implies (nothing to change)
 *   exceeds   → the next drier family than the declared sink implies; an
 *               already air-cooled site gets none — the limit, not the family,
 *               is the problem
 *   undocumented / not-applicable / unknown sink → none
 */
export interface DataCenterCoolingRecommendation {
  coolingType: DataCenterCoolingType;
  reason: string;
}

const SINK_TO_COOLING_FAMILY: Readonly<Record<string, DataCenterCoolingType | null>> = {
  'wet-cooling-tower': 'evaporative',
  hybrid: 'hybrid',
  'dry-cooler': 'air-cooled',
  'free-cooling': 'air-cooled',
  'once-through': null,
  'absorption-cchp': null,
  unknown: null,
};

const DRIER_FAMILY: Readonly<Record<DataCenterCoolingType, DataCenterCoolingType | null>> = {
  evaporative: 'hybrid',
  hybrid: 'air-cooled',
  'air-cooled': null,
};

export function recommendDataCenterCoolingType(args: {
  declaredHeatRejection: string | null | undefined;
  waterFit: 'fits' | 'exceeds' | 'undocumented' | 'not-applicable' | null | undefined;
}): DataCenterCoolingRecommendation | null {
  const family = args.declaredHeatRejection ? SINK_TO_COOLING_FAMILY[args.declaredHeatRejection] ?? null : null;
  if (!family) return null;
  if (args.waterFit === 'fits') {
    return {
      coolingType: family,
      reason: `Matches the ${args.declaredHeatRejection} heat-rejection sink declared in Project Intent; the documented water limit is met.`,
    };
  }
  if (args.waterFit === 'exceeds') {
    const drier = DRIER_FAMILY[family];
    if (!drier) return null;
    return {
      coolingType: drier,
      reason: `Peak consumption exceeds the documented water limit with ${family} cooling; ${drier} is the next drier family.`,
    };
  }
  return null;
}

export function dataCenterPreset(id: WorkloadProfile): DataCenterPreset | null {
  return dataCenterPresetsForIntent(null).find(preset => preset.id === id) ?? null;
}

/**
 * Size a preset into the load ladder. `kwPerRack` defaults to the profile's
 * mid-band anchor; an explicit value is clamped to the profile's published
 * range so a preset can never claim a density its workload does not support.
 */
export function sizeDataCenter(params: {
  presetId: WorkloadProfile;
  rackCount: number;
  kwPerRack?: number;
  pue?: number;
  powerFactor?: number;
}): DataCenterSizing {
  const preset = dataCenterPreset(params.presetId);
  if (!preset) throw new RangeError(`Unknown data-centre preset: ${params.presetId}`);
  if (!Number.isFinite(params.rackCount) || params.rackCount <= 0) {
    throw new RangeError('A data centre requires a positive rack count.');
  }
  const requested = params.kwPerRack ?? preset.defaultKwPerRack;
  if (!Number.isFinite(requested) || requested <= 0) {
    throw new RangeError('Rack power density must be a positive kW figure.');
  }
  const kwPerRack = Math.min(
    preset.powerDensityKwRack.max,
    Math.max(preset.powerDensityKwRack.min, requested),
  );
  const itLoadMW = params.rackCount * kwPerRack / 1_000;
  // The platform's power ladder owns PUE + power-factor conversion.
  const power = resolvePowerStack({
    enteredMW: itLoadMW,
    basis: 'it-load',
    ...(params.pue == null ? {} : { pue: params.pue }),
    ...(params.powerFactor == null ? {} : { powerFactor: params.powerFactor }),
  });
  return {
    presetId: preset.id,
    label: preset.label,
    rackCount: params.rackCount,
    kwPerRack,
    itLoadMW,
    facilityMW: power.facilityMW ?? itLoadMW,
    gridMW: power.gridMW,
    pue: power.pue,
    power,
  };
}

/**
 * Drive the scenario's duty from a sized data centre: the facility load becomes
 * what generation must serve. `criticalLoadMW` follows the facility load by
 * default (an islanded data centre is critical in full); pass
 * `criticalFraction` to back a smaller critical block.
 *
 * Returns a NEW input — the caller re-solves. Everything else on the scenario
 * (units, CCS, footprint, receptors) is untouched.
 */
export type DataCenterCoolingType = 'air-cooled' | 'evaporative' | 'hybrid';

/**
 * Screening-grade water-intensity defaults per data-centre cooling family,
 * authored in the literature's native unit (Green Grid WUE, litres of site
 * water per kWh of IT energy) and converted through the scenario PUE by
 * {@link deriveDataCenterCoolingWaterBasis}. LOW-confidence planning defaults:
 * they exist so the campus water total can compute at screening, and every
 * application carries `source: 'planning-default'` with the citation so an
 * engineered or evidence-supplied basis supersedes them.
 */
export interface DataCenterCoolingWaterDefault {
  coolingType: DataCenterCoolingType;
  label: string;
  /** Green Grid WUE convention: litres per kWh of IT energy. */
  litresPerKwhIt: { withdrawal: number; consumption: number };
  citation: string;
  confidence: 'low';
}

export const DATA_CENTER_COOLING_WATER_DEFAULTS: readonly DataCenterCoolingWaterDefault[] = [
  {
    coolingType: 'air-cooled',
    label: 'Air-cooled (dry)',
    litresPerKwhIt: { withdrawal: 0.1, consumption: 0.1 },
    citation: 'Screening default — air-cooled direct WUE ≈ 0.1 L/kWh IT (humidification and make-up only); Green Grid WUE convention, LBNL United States Data Center Energy Usage Report lineage.',
    confidence: 'low',
  },
  {
    coolingType: 'evaporative',
    label: 'Evaporative / water-cooled',
    litresPerKwhIt: { withdrawal: 2.2, consumption: 1.8 },
    citation: 'Screening default — evaporative-cooled site WUE ≈ 1.8 L/kWh IT consumption (Shehabi et al., LBNL United States Data Center Energy Usage Report lineage); withdrawal adds cycles-of-concentration blowdown.',
    confidence: 'low',
  },
  {
    coolingType: 'hybrid',
    label: 'Hybrid (economizer + trim evaporative)',
    litresPerKwhIt: { withdrawal: 1.1, consumption: 0.9 },
    citation: 'Screening default — midpoint of the air-cooled and evaporative WUE anchors for economizer-led plants with evaporative trim; Green Grid WUE convention.',
    confidence: 'low',
  },
];

/**
 * Convert a cooling-family WUE default into the scenario's water basis
 * (gal per MWh of facility load) through the scenario PUE. Returns null when
 * the cooling type is unknown or the PUE is outside the converter's guard
 * (1–3) — the caller falls back to manual entry.
 */
export function deriveDataCenterCoolingWaterBasis(
  coolingType: DataCenterCoolingType,
  pue: number,
): {
  coolingType: DataCenterCoolingType;
  label: string;
  withdrawalGalPerMWhFacility: number;
  consumptionGalPerMWhFacility: number;
  citation: string;
} | null {
  const entry = DATA_CENTER_COOLING_WATER_DEFAULTS.find(candidate => candidate.coolingType === coolingType);
  if (!entry) return null;
  const withdrawal = convertLitresPerKwhItToGalPerMwhFacility(entry.litresPerKwhIt.withdrawal, pue);
  const consumption = convertLitresPerKwhItToGalPerMwhFacility(entry.litresPerKwhIt.consumption, pue);
  if (withdrawal == null || consumption == null) return null;
  return {
    coolingType: entry.coolingType,
    label: entry.label,
    withdrawalGalPerMWhFacility: withdrawal,
    consumptionGalPerMWhFacility: consumption,
    citation: entry.citation,
  };
}

export function applyDataCenterLoad(
  source: GenerationImpactScenarioInput,
  sizing: DataCenterSizing,
  options: { criticalFraction?: number } = {},
): GenerationImpactScenarioInput {
  const fraction = options.criticalFraction ?? 1;
  if (!Number.isFinite(fraction) || fraction <= 0 || fraction > 1) {
    throw new RangeError('The critical-load fraction must be greater than 0 and at most 1.');
  }
  return {
    ...structuredClone(source),
    facilityLoadMW: sizing.facilityMW,
    criticalLoadMW: sizing.facilityMW * fraction,
  };
}
